import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';

export { default as Avatar } from '@/components/common/Avatar';

/* ── Presence ───────────────────────────────────────────────────────────── */
export function PresenceDot({
  isOnline,
  isBusy,
  showLabel = true,
}: {
  isOnline: boolean;
  isBusy: boolean;
  showLabel?: boolean;
}) {
  const color = !isOnline ? 'var(--muted)' : isBusy ? 'var(--busy)' : 'var(--online)';
  const label = !isOnline ? 'Offline' : isBusy ? 'In a session' : 'Free now';

  return (
    <span className="inline-flex items-center gap-1.5 text-xs font-medium" style={{ color }}>
      <span className="relative flex size-2">
        {isOnline && !isBusy && (
          <span
            className="absolute inline-flex size-full rounded-full opacity-60"
            style={{ background: color, animation: 'breathe 2.6s ease-in-out infinite' }}
          />
        )}
        <span className="relative inline-flex size-2 rounded-full" style={{ background: color }} />
      </span>
      {showLabel && label}
    </span>
  );
}

/* ── Badge ──────────────────────────────────────────────────────────────── */
export function Badge({
  children,
  tone = 'neutral',
}: {
  children: ReactNode;
  tone?: 'neutral' | 'brand' | 'marigold' | 'teal' | 'online' | 'verified';
}) {
  const bg = {
    neutral: 'var(--surface-2)',
    brand: 'var(--brand-soft)',
    marigold: 'var(--brand-soft)',
    teal: 'var(--teal-soft)',
    online: 'var(--online-soft)',
    verified: '#e7f7ef',
  }[tone];
  const fg = {
    neutral: 'var(--ink-2)',
    brand: 'var(--brand)',
    marigold: 'var(--brand)',
    teal: 'var(--teal)',
    online: 'var(--online)',
    verified: '#0f7a48',
  }[tone];

  return (
    <span
      className="inline-block rounded-full px-2.5 py-1 text-xs leading-none font-semibold"
      style={{ background: bg, color: fg }}
    >
      {children}
    </span>
  );
}

/* ── Button ─────────────────────────────────────────────────────────────── */
type ButtonProps = {
  children: ReactNode;
  variant?: 'primary' | 'secondary' | 'ghost' | 'gradient';
  size?: 'sm' | 'md' | 'lg';
  to?: string;
  onClick?: () => void;
  disabled?: boolean;
  full?: boolean;
  type?: 'button' | 'submit';
};

const SIZES = {
  sm: 'px-3.5 py-1.5 text-sm rounded-lg',
  md: 'px-5 py-2.5 text-sm rounded-xl font-semibold',
  lg: 'px-7 py-3.5 text-base rounded-full font-semibold',
};

export function Button({
  children,
  variant = 'primary',
  size = 'md',
  to,
  onClick,
  disabled,
  full,
  type = 'button',
}: ButtonProps) {
  const base = `inline-flex items-center justify-center gap-2 transition-all duration-150 disabled:cursor-not-allowed disabled:opacity-45 ${SIZES[size]} ${full ? 'w-full' : ''}`;

  if (variant === 'gradient') {
    const cls = `${base} btn-grad text-white shadow-sm`;
    if (to && !disabled) {
      return (
        <Link to={to} className={cls}>
          {children}
        </Link>
      );
    }
    return (
      <button type={type} className={cls} onClick={onClick} disabled={disabled}>
        {children}
      </button>
    );
  }

  const styles: Record<string, React.CSSProperties> = {
    primary: {
      background: 'var(--brand)',
      color: '#fff',
      boxShadow: '0 1px 2px rgb(10 31 99 / 0.15), inset 0 1px 0 rgb(255 255 255 / 0.16)',
    },
    secondary: {
      background: 'var(--surface)',
      color: 'var(--navy)',
      border: '1px solid var(--line)',
      boxShadow: 'var(--shadow-sm)',
    },
    ghost: { background: 'transparent', color: 'var(--ink-2)' },
  };

  const hover =
    variant === 'primary'
      ? 'hover:bg-[var(--brand-dark)] hover:-translate-y-px active:translate-y-0'
      : variant === 'secondary'
        ? 'hover:bg-[var(--surface-2)] hover:border-navy hover:-translate-y-px active:translate-y-0'
        : 'hover:bg-[var(--surface-2)]';

  const cls = `${base} ${disabled ? '' : hover}`;

  if (to && !disabled) {
    return (
      <Link to={to} className={cls} style={styles[variant]}>
        {children}
      </Link>
    );
  }
  return (
    <button
      type={type}
      className={cls}
      style={styles[variant]}
      onClick={onClick}
      disabled={disabled}
    >
      {children}
    </button>
  );
}

/* ── Card ───────────────────────────────────────────────────────────────── */
export function Card({
  children,
  className = '',
  raised = false,
  as: Tag = 'div',
  style,
  onClick,
}: {
  children: ReactNode;
  className?: string;
  raised?: boolean;
  as?: 'div' | 'section' | 'li';
  style?: React.CSSProperties;
  onClick?: (e: React.MouseEvent) => void;
}) {
  return (
    <Tag
      className={`rounded-2xl ${raised ? 'surface-raised' : 'surface'} ${className}`}
      style={style}
      onClick={onClick}
    >
      {children}
    </Tag>
  );
}


/* ── Section heading ────────────────────────────────────────────────────── */
export function SectionHeading({
  children,
  eyebrow,
  action,
}: {
  children: ReactNode;
  eyebrow?: string;
  action?: ReactNode;
}) {
  return (
    <div className="mb-5 flex items-end justify-between gap-4">
      <div>
        {eyebrow && <p className="eyebrow mb-1.5">{eyebrow}</p>}
        <h2 className="text-2xl font-semibold">{children}</h2>
      </div>
      {action && <div className="shrink-0 pb-1">{action}</div>}
    </div>
  );
}

/* ── Verified mark ──────────────────────────────────────────────────────── */
export function VerifiedMark({ size = 16 }: { size?: number }) {
  return (
    <svg viewBox="0 0 16 16" width={size} height={size} className="shrink-0" aria-label="Verified">
      <path
        fill="var(--verified)"
        d="M8 1l1.9 1.4 2.3-.2.6 2.2 1.9 1.3-1 2.1 1 2.1-1.9 1.3-.6 2.2-2.3-.2L8 15l-1.9-1.4-2.3.2-.6-2.2L1.3 10l1-2.1-1-2.1 1.9-1.3.6-2.2 2.3.2z"
      />
      <path
        fill="none"
        stroke="var(--surface)"
        strokeWidth="1.7"
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M5.4 8.1l1.8 1.8 3.4-3.6"
      />
    </svg>
  );
}

/* ── Coin ───────────────────────────────────────────────────────────────── */
export function Coin({ className = '' }: { className?: string }) {
  return (
    <svg viewBox="0 0 16 16" className={`inline-block size-[1em] ${className}`} aria-hidden>
      <circle cx="8" cy="8" r="7" fill="currentColor" opacity="0.18" />
      <circle cx="8" cy="8" r="7" fill="none" stroke="currentColor" strokeWidth="1.3" />
      <path
        d="M6 5h4M6 7.4h4M9.4 5c0 2.6-2 2.4-3.4 2.4L9.6 11"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.4"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/* ── Star rating ────────────────────────────────────────────────────────── */
export function Rating({ value, count }: { value: number; count?: number }) {
  return (
    <span className="inline-flex items-baseline gap-1 text-sm">
      <svg viewBox="0 0 16 16" className="size-3.5 self-center" aria-hidden>
        <path
          fill="var(--marigold)"
          d="M8 1.6l1.9 3.9 4.3.6-3.1 3 .7 4.3L8 11.4 4.2 13.4l.7-4.3-3.1-3 4.3-.6z"
        />
      </svg>
      <span className="font-semibold tabular-nums">{value.toFixed(1)}</span>
      {count !== undefined && (
        <span className="text-xs text-[var(--muted)] tabular-nums">({count})</span>
      )}
    </span>
  );
}
